import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Button, Card, Input, useToast } from '@caruso/ui';
import { companiesService } from '@/services/companies.service';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Building,
} from 'lucide-react';
import { cn } from '@caruso/ui';

export function CompaniesPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['companies', { search, page }],
    queryFn: () =>
      companiesService.getAll({
        search,
        page,
        pageSize: 10,
      }),
  });

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir esta empresa?')) return;

    try {
      await companiesService.delete(id);
      toast({
        title: 'Empresa excluída',
        variant: 'success',
      });
      refetch();
    } catch (error) {
      toast({
        title: 'Erro ao excluir empresa',
        variant: 'destructive',
      });
    }
  };

  const formatPhone = (phone?: string) => {
    if (!phone) return '';
    return phone.replace(/(\d{2})(\d{4,5})(\d{4})/, '($1) $2-$3');
  };

  const formatCNPJ = (cnpj?: string) => {
    if (!cnpj) return '';
    return cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-serif text-brand-gold">Empresas</h1>
          <p className="text-brand-ivory/60 mt-1">
            Gerencie o cadastro de empresas
          </p>
        </div>
        <Button onClick={() => navigate('/app/empresas/nova')} variant="premium">
          <Plus className="w-4 h-4 mr-2" />
          Nova Empresa
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-brand-ink/50 border-brand-gold/20">
        <div className="p-4 flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-gold/50" />
            <Input
              placeholder="Buscar por nome, CNPJ, telefone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
            />
          </div>
        </div>
      </Card>

      {/* Companies List */}
      {isLoading ? (
        <div className="text-center py-12 text-brand-ivory/50">
          Carregando...
        </div>
      ) : (
        <>
          <div className="grid gap-4">
            {data?.data.map((company) => (
              <motion.div
                key={company.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ scale: 1.01 }}
              >
                <Card className="bg-brand-ink/50 border-brand-gold/20 hover:border-brand-gold/40 transition-all cursor-pointer">
                  <div className="p-6 flex items-center justify-between" onClick={() => navigate(`/app/empresas/${company.id}`)}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Building className="w-5 h-5 text-brand-gold" />
                        <h3 className="text-lg font-serif text-brand-gold">
                          {company.name}
                        </h3>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-4 text-sm text-brand-ivory/60">
                        {company.cnpj && (
                          <span>CNPJ: {formatCNPJ(company.cnpj)}</span>
                        )}
                        {company.mainPhone && (
                          <span>{formatPhone(company.mainPhone)}</span>
                        )}
                        {company.city && company.state && (
                          <span>{company.city}/{company.state}</span>
                        )}
                      </div>
                      {company.address && (
                        <div className="mt-2 text-sm text-brand-ivory/50">
                          {company.address}
                          {company.number && `, ${company.number}`}
                          {company.complement && ` - ${company.complement}`}
                          {company.neighborhood && `, ${company.neighborhood}`}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate(`/app/empresas/${company.id}`)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(company.id)}
                      >
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </Button>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Pagination */}
          {data && data.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="px-4 text-brand-ivory">
                Página {page} de {data.totalPages}
              </span>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setPage(page + 1)}
                disabled={page === data.totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}

          {data?.data.length === 0 && (
            <div className="text-center py-12">
              <Building className="w-16 h-16 mx-auto text-brand-gold/20 mb-4" />
              <p className="text-brand-ivory/50">Nenhuma empresa encontrada</p>
              <Button
                onClick={() => navigate('/app/empresas/nova')}
                variant="outline"
                className="mt-4"
              >
                <Plus className="w-4 h-4 mr-2" />
                Adicionar primeira empresa
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}